/**
 * ============================================================================
 *  File        : frontend/src/context/AuthContext.tsx
 *  Module      : context
 *  Purpose     : React auth/session context，取代 Pinia auth store
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { configureApi, api } from '../api/client'

interface AuthState {
  accessToken: string | null
  userLabel: string
  roles: string[]
  permissions: string[]
  isReady: boolean
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const STORAGE_KEY = 'ef-platform.session'

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<AuthState>(() => {
    const saved = readSavedSession()
    return {
      accessToken: saved?.accessToken ?? null,
      userLabel: saved?.userLabel ?? '',
      roles: [],
      permissions: [],
      isReady: false,
    }
  })

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setState({ accessToken: null, userLabel: '', roles: [], permissions: [], isReady: true })
  }, [])

  useEffect(() => {
    configureApi({
      getToken: () => readSavedSession()?.accessToken ?? null,
      onUnauthorized: reset,
    })
  }, [reset])

  useEffect(() => {
    let active = true
    async function bootstrap(): Promise<void> {
      if (!state.accessToken) {
        setState((current) => ({ ...current, isReady: true }))
        return
      }
      try {
        const me = await api.me()
        if (!active) return
        const userLabel = readUserLabel(me) || state.userLabel
        const roles = readStringArray(me, 'roles')
        const permissions = readStringArray(me, 'permissions')
        saveSession({ accessToken: state.accessToken, userLabel })
        setState({
          accessToken: state.accessToken,
          userLabel,
          roles,
          permissions,
          isReady: true,
        })
      } catch {
        if (active) reset()
      }
    }
    void bootstrap()
    return () => {
      active = false
    }
  }, [reset, state.accessToken, state.userLabel])

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await api.login({ email, password })
    const accessToken = readAccessToken(tokens)
    const userLabel = email
    saveSession({ accessToken, userLabel })
    // roles / permissions 由 accessToken 變更觸發的 /me bootstrap effect 補齊
    setState({ accessToken, userLabel, roles: [], permissions: [], isReady: false })
  }, [])

  const logout = useCallback(async () => {
    try {
      if (readSavedSession()?.accessToken) {
        await api.logout()
      }
    } finally {
      reset()
    }
  }, [reset])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: Boolean(state.accessToken),
      login,
      logout,
    }),
    [login, logout, state],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('AuthContext is not mounted')
  }
  return value
}

function readSavedSession(): { accessToken: string; userLabel: string } | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const record = parsed as Record<string, unknown>
    const accessToken = record['accessToken']
    const userLabel = record['userLabel']
    if (typeof accessToken !== 'string') return null
    return {
      accessToken,
      userLabel: typeof userLabel === 'string' ? userLabel : '',
    }
  } catch {
    return null
  }
}

function saveSession(session: { accessToken: string; userLabel: string }): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

function readAccessToken(payload: unknown): string {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error('missing access token')
  }
  const record = payload as Record<string, unknown>
  const snake = record['access_token']
  const camel = record['accessToken']
  if (typeof snake === 'string') return snake
  if (typeof camel === 'string') return camel
  throw new Error('missing access token')
}

function readStringArray(payload: unknown, key: string): string[] {
  if (typeof payload !== 'object' || payload === null) return []
  const value = (payload as Record<string, unknown>)[key]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function readUserLabel(payload: unknown): string {
  if (typeof payload !== 'object' || payload === null) return ''
  const record = payload as Record<string, unknown>
  const email = record['email']
  const name = record['name']
  const displayName = record['display_name']
  if (typeof displayName === 'string') return displayName
  if (typeof name === 'string') return name
  if (typeof email === 'string') return email
  return ''
}
